import React, { createContext, useContext, useRef, useCallback } from "react";
import useNetworkStatus from "../hooks/useNetworkStatus";

/**
 * Provides a single shared `isOnline` boolean.
 * Components can call `useNetworkStatusChange(fn)` to subscribe to
 * online/offline transitions (e.g., to show a toast).
 */
const NetworkContext = createContext({ isOnline: true, subscribe: () => () => {} });

export function NetworkProvider({ children }) {
  const listenersRef = useRef(new Set());

  const handleStatusChange = useCallback((online) => {
    listenersRef.current.forEach((fn) => fn(online));
  }, []);

  const isOnline = useNetworkStatus(handleStatusChange);

  const subscribe = useCallback((fn) => {
    listenersRef.current.add(fn);
    return () => listenersRef.current.delete(fn);
  }, []);

  return (
    <NetworkContext.Provider value={{ isOnline, subscribe }}>
      {children}
    </NetworkContext.Provider>
  );
}

/** Returns current online status. */
export function useNetworkContext() {
  return useContext(NetworkContext).isOnline;
}

/**
 * Subscribe to online/offline transitions.
 * @param {Function} fn - called with (isOnline: boolean) on each change
 */
export function useNetworkStatusChange(fn) {
  const { subscribe } = useContext(NetworkContext);
  const fnRef = useRef(fn);
  fnRef.current = fn;

  React.useEffect(() => {
    return subscribe((online) => fnRef.current(online));
  }, [subscribe]);
}
